/**
 * notificationsSlice — the gw-spa notifications zustand slice (PRP-PLAT-026
 * FR-2/FR-4/NFR-Performance, GW-8886).
 *
 * Why a standalone zustand store, not a slice folded into `src/stores/store.ts`:
 * the combined `useStore` persists a curated subset of fields via `partialize`
 * and every one of its ~40 slices shares one `set`/`get` pair. Notifications
 * are explicitly session-lived, never persisted (spec's Non-Goals — no
 * history endpoint exists upstream), and arrive at high frequency
 * (`progress`/`phase_changed`, up to ~42+ events/run per the PRP's NFR).
 * Keeping this store separate means a notification flood never touches
 * `useStore`'s `persist`/`devtools` middleware pipeline and never risks a
 * stray `partialize` field leaking ephemeral notification state into
 * localStorage. The PRP's own architecture diagram places this file under
 * `src/lib/notifications/`, a sibling of `toNotificationRecord.ts` and
 * `notificationRoute.ts`, not under `src/stores/slices/` — this mirrors that
 * placement.
 *
 * Selector discipline (NFR-Performance): high-frequency `progress`/
 * `phase_changed` ingest MUST NOT re-render the app shell. This store uses
 * the `subscribeWithSelector` middleware so both React consumers
 * (`useNotificationsSlice(selector)`, which already only re-renders on a
 * changed selector output via zustand's default `Object.is` check) and
 * non-React subscribers (`useNotificationsSlice.subscribe(selector,
 * listener)`, used by the selector-discipline test below) get the same
 * guarantee: a listener/re-render fires only when the SELECTED value
 * actually changes, not on every `set()`. `useNotificationsUnreadCount()`
 * selects a derived primitive (a count), so ingesting a read notification's
 * updated content — or coalescing a `progress` event that doesn't change
 * the unread count — never triggers the bell to re-render.
 *
 * Bounded ingest (FR-2): retains at most `MAX_RETAINED_NOTIFICATIONS`
 * (200, per the PRP's suggestion) records, newest first, deduped on `id`
 * (re-ingesting an id already present is a no-op — at-least-once AppSync
 * delivery is safe to feed straight through, matching `@gw/ui`'s own
 * `useNotificationsStore` dedupe contract). `progress`/`phase_changed`
 * records additionally coalesce per `runId`: a new `progress` event for a
 * run replaces that run's prior `progress` entry rather than appending
 * another one, so a long-running mission's flood of progress ticks cannot
 * grow the feed unbounded on its own — the N=200 cap is a backstop for
 * everything else (multiple runs, `done`/`failed`/`budget_warning`, etc.).
 *
 * Ingest pipeline (FR-4): `ingestWireNotification` is the single seam a
 * future connection manager (GW-8887, out of scope here) will call from its
 * `onEvent` handler — wire event -> `toNotificationRecord` (GW-8885) ->
 * `ingest` -> `getNotificationToastSpec(record, linkTo)` (`@gw/ui`) ->
 * sonner dispatch. This file is tested end-to-end against injected fake
 * wire events; no real socket is involved (that is GW-8887's job).
 *
 * No wire-payload logging (Security NFR): nothing in this file calls
 * `console.*` or any Sentry breadcrumb/capture API. Every value that
 * touches the wire payload (the record's `payload` field, the raw
 * `wireEvent`) is used only for adaptation, storage, and toast text — never
 * passed to a logging sink.
 */
import { getNotificationToastSpec } from '@gw/ui';
import { toast } from 'sonner';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

import { notificationRoute } from '@/lib/notifications/notificationRoute';
import { toNotificationRecord } from '@/lib/notifications/toNotificationRecord';

import type { WireNotificationEvent } from '@/lib/notifications/toNotificationRecord';
import type { NotificationContext, NotificationRecord, NotificationToastSpec } from '@gw/ui';

// ---------------------------------------------------------------------------
// Retention + coalescing
// ---------------------------------------------------------------------------

/** Most-recent-N retention bound at the ingest seam (spec FR-2; N=200 suggested). */
export const MAX_RETAINED_NOTIFICATIONS = 200;

/**
 * Wire types that coalesce per `runId` instead of accumulating one record
 * per event. Both are the PRP's own named examples of high-frequency,
 * no-toast, "inline update" event types (spec §4.1's "No toast" rows).
 */
const COALESCIBLE_TYPES: ReadonlySet<string> = new Set([
  'mission.run.progress',
  'mission.run.phase_changed',
]);

/**
 * The coalescing key for a record, or `undefined` if this record never
 * coalesces (either its type isn't in `COALESCIBLE_TYPES`, or it has no
 * `mission-run` context to key on — a malformed/contextless record of a
 * coalescible type is not coalesced, it just accumulates like any other
 * record; the N=200 cap still bounds it).
 */
function coalesceKey(record: NotificationRecord): string | undefined {
  if (!COALESCIBLE_TYPES.has(record.type)) return undefined;
  if (record.context?.kind !== 'mission-run') return undefined;
  return `${record.type}:${record.context.runId}`;
}

/**
 * Pure reducer: applies one incoming record to the existing (newest-first)
 * list, per the dedupe/coalesce/retention rules documented on this module.
 * Exported for direct unit testing without going through the store.
 */
export function reduceIngest(
  existing: readonly NotificationRecord[],
  incoming: NotificationRecord
): NotificationRecord[] {
  // Dedupe on id: re-ingesting an id already present is a no-op.
  if (existing.some((n) => n.id === incoming.id)) {
    return existing as NotificationRecord[];
  }

  let next: NotificationRecord[] = existing as NotificationRecord[];
  const key = coalesceKey(incoming);
  if (key !== undefined) {
    next = next.filter((n) => coalesceKey(n) !== key);
  }

  next = [incoming, ...next];
  if (next.length > MAX_RETAINED_NOTIFICATIONS) {
    next = next.slice(0, MAX_RETAINED_NOTIFICATIONS);
  }
  return next;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export interface NotificationsSliceState {
  /** All retained notifications, newest first. */
  notifications: NotificationRecord[];
  /** Inserts one adapted record — dedupe/coalesce/retention per `reduceIngest`. */
  ingest: (record: NotificationRecord) => void;
  markRead: (id: string) => void;
  markAllRead: () => void;
  dismiss: (id: string) => void;
}

export const useNotificationsSlice = create<NotificationsSliceState>()(
  subscribeWithSelector((set) => ({
    notifications: [],

    ingest: (record) =>
      set((state) => ({ notifications: reduceIngest(state.notifications, record) })),

    markRead: (id) =>
      set((state) => ({
        notifications: state.notifications.map((n) => (n.id === id ? { ...n, read: true } : n)),
      })),

    markAllRead: () =>
      set((state) => ({
        notifications: state.notifications.map((n) => (n.read ? n : { ...n, read: true })),
      })),

    dismiss: (id) =>
      set((state) => ({
        notifications: state.notifications.filter((n) => n.id !== id),
      })),
  }))
);

// ---------------------------------------------------------------------------
// Narrow selectors (NFR-Performance — one hook per surface, never the whole
// state object) — see this module's docstring for why re-render discipline
// falls out of selecting a primitive/stable value rather than any special
// memoization.
// ---------------------------------------------------------------------------

/** Derived unread count. A primitive selector output — the bell only re-renders when the COUNT changes. */
export function selectUnreadCount(state: NotificationsSliceState): number {
  return state.notifications.reduce((count, n) => count + (n.read ? 0 : 1), 0);
}

/** The bell's sole subscription — never the full notifications array. */
export function useNotificationsUnreadCount(): number {
  return useNotificationsSlice(selectUnreadCount);
}

/** The feed page's subscription — the only surface allowed to read the full array. */
export function useNotificationsFeed(): NotificationRecord[] {
  return useNotificationsSlice((state) => state.notifications);
}

/** Combined action selector (stable function references; `useShallow` avoids a new-object-per-render re-render). */
export function useNotificationsActions(): Pick<
  NotificationsSliceState,
  'markRead' | 'markAllRead' | 'dismiss'
> {
  return useNotificationsSlice(
    useShallow((state) => ({
      markRead: state.markRead,
      markAllRead: state.markAllRead,
      dismiss: state.dismiss,
    }))
  );
}

// ---------------------------------------------------------------------------
// Ingest pipeline — wire event -> record -> store -> toast (FR-4)
// ---------------------------------------------------------------------------

/**
 * Bridges `notificationRoute` (FR-3, returns a bare route string or
 * `undefined`) into the shape `getNotificationToastSpec`'s `linkTo`
 * parameter requires (`{ onClick?: () => void; href?: string }`, per
 * `@gw/ui`'s real `notifications.types.d.ts`/`notificationDisplay.d.ts` —
 * verified against the installed `@gw/ui` 3.14.0 package, not assumed).
 * `notificationRoute` never throws, so this never throws either.
 */
function routeToLinkTo(context: NotificationContext): { href?: string } {
  const href = notificationRoute(context);
  return href ? { href } : {};
}

/**
 * Navigates by full page load. Mirrors the existing out-of-component
 * navigation precedent in `src/hooks/useAuthErrorHandler.ts` (GW-2099) —
 * this module's toast dispatch runs from an imperative ingest path (a
 * future socket `onEvent` callback, GW-8887), not from inside a React
 * component, so `useNavigate()` is unavailable here.
 */
function navigateTo(href: string): void {
  window.location.href = href;
}

/**
 * Adapts `getNotificationToastSpec`'s `action` (`{ label, onClick?, href?
 * }`, gw-ui's routing-free shape) into sonner's `Action` (`{ label,
 * onClick }` — sonner has no `href` field). `spec.action.onClick` will
 * never actually be set today (`routeToLinkTo` only ever returns `href`),
 * but this still prefers an explicit `onClick` if `@gw/ui` ever supplies
 * one directly.
 */
function toSonnerAction(
  action: NotificationToastSpec['action']
): { label: string; onClick: () => void } | undefined {
  if (!action) return undefined;
  if (action.onClick) return { label: action.label, onClick: action.onClick };
  const { href } = action;
  if (href) return { label: action.label, onClick: () => navigateTo(href) };
  return undefined;
}

/**
 * Dispatches the toast for a `NotificationToastSpec` via the existing
 * sonner `toast` (the `<Toaster>` is already mounted once in `src/App.tsx`
 * — this module does not mount its own). `spec.variant` is a
 * `NotificationSeverity` (`success`/`error`/`warning`/`info`), which maps
 * 1:1 onto sonner's `toast.success`/`toast.error`/`toast.warning`/
 * `toast.info` methods.
 */
function dispatchNotificationToast(spec: NotificationToastSpec): void {
  toast[spec.variant](spec.title, {
    description: spec.description,
    action: toSonnerAction(spec.action),
  });
}

/**
 * The full FR-4 ingest pipeline for one decoded wire event: adapt ->
 * store -> toast-iff-non-null. This is the seam GW-8887's connection
 * manager will call from its `onEvent` handler; here it is exercised
 * directly with injected fake `WireNotificationEvent`s (no socket).
 *
 * Never throws: `toNotificationRecord` and `notificationRoute` are both
 * documented never-throw, and `getNotificationToastSpec` is a pure mapping
 * function over data this module already validated is well-typed.
 *
 * @returns the adapted `NotificationRecord`, mainly so tests can assert
 *   against it without re-deriving the adaptation themselves.
 */
export function ingestWireNotification(wireEvent: WireNotificationEvent): NotificationRecord {
  const record = toNotificationRecord(wireEvent);
  useNotificationsSlice.getState().ingest(record);

  const spec = getNotificationToastSpec(record, routeToLinkTo);
  if (spec) {
    dispatchNotificationToast(spec);
  }

  return record;
}
